import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface PrefillDataEntry {
  clientPolicyApplicationId: string;
  formId: string;
  clientId: string;
  prefillData: PrefillData[];
  citations: PrefillCitations[];
  status: string;
  error: string | null;
}

export interface PolicyApplicationPrefillState {
  entries: PrefillDataEntry[];
}

const initialState: PolicyApplicationPrefillState = {
  entries: [],
};

const prefillDataSlice = createSlice({
  name: 'policyApplicationPrefill',
  initialState,
  reducers: {
    populatePrefillData(state, action: PayloadAction<PrefillDataEntry>) {
      const { clientId, clientPolicyApplicationId, formId, prefillData, citations } =
        action.payload;

      const existingIndex = state.entries.findIndex(
        entry =>
          entry.clientId === clientId &&
          entry.clientPolicyApplicationId === clientPolicyApplicationId &&
          entry.formId === formId,
      );
      if (existingIndex !== -1) {
        state.entries[existingIndex] = {
          ...state.entries[existingIndex],
          prefillData,
          citations,
        };
      } else {
        state.entries.push({
          clientId,
          clientPolicyApplicationId,
          formId,
          prefillData,
          citations,
          status: 'populated',
          error: null,
        });
      }
    },
    removePrefillData: (
      state,
      action: PayloadAction<{
        clientId: string;
        clientPolicyApplicationId: string;
        formId: string;
      }>,
    ) => {
      const { clientId, clientPolicyApplicationId, formId } = action.payload;
      state.entries = state.entries.filter(
        entry =>
          !(
            entry.clientId === clientId &&
            entry.clientPolicyApplicationId === clientPolicyApplicationId &&
            entry.formId === formId
          ),
      );
    },
    resetPrefillDataState: state => (state = initialState),
    updatePolicyPrefillData: (
      state,
      action: PayloadAction<{
        clientPolicyApplicationId: string;
        formId: string;
        prefillData: PrefillData[];
      }>,
    ) => {
      const { clientPolicyApplicationId, formId, prefillData } = action.payload;

      // Find existing entry that matches clientPolicyApplicationId and formId
      const existingIndex = state.entries.findIndex(
        entry =>
          entry.clientPolicyApplicationId === clientPolicyApplicationId && entry.formId === formId,
      );

      if (existingIndex !== -1) {
        // Merge with existing prefill data, overwriting duplicates
        const existing = state.entries[existingIndex].prefillData || [];
        const updatedMap = new Map(existing.map(item => [item.questionRef, item]));

        prefillData.forEach(item => {
          updatedMap.set(item.questionRef, item);
        });

        state.entries[existingIndex].prefillData = Array.from(updatedMap.values());
      } else {
        // No existing entry found, create a new one
        state.entries.push({
          clientId: '', // Will be populated when form is actually loaded
          clientPolicyApplicationId,
          formId,
          prefillData,
          citations: [],
          status: 'populated',
          error: null,
        });
      }
    },
  },
});

export const {
  populatePrefillData,
  removePrefillData,
  resetPrefillDataState,
  updatePolicyPrefillData,
} = prefillDataSlice.actions;

export const policyApplicationPrefillReducer = prefillDataSlice.reducer;
